import {Component, NgZone, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {delay, filter, repeatWhen, take} from 'rxjs/operators';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {Subscription} from 'rxjs';
import {OptimizationReportService} from '../../../../services/optimization-report.service';
import {ScenarioService} from '../../../../services/scenario.service';
import {UiBlockerService} from '../../../../services/ui-blocker.service';
import {ModelRunService} from '../../../../services/model-run.service';
import {GenericConfirmationModalComponent} from '../../../../components/generic-confirmation-modal/generic-confirmation-modal.component';
import {OptimizationReport} from '../../../../models/optimization-report.model';
import {ModelRun} from '../../../../models/model-run.model';
import {SnackBarService} from '../../../../components/snack-bar/snack-bar.service';
import {MetaData} from '../../../../models/meta-data.model';
import {AppConstantsService} from '@app/services/app-constants.service';
import {WebMessageService} from "@app/services/web-message.service";
import { sanitizeFileName } from 'src/app/utils/object-utils';
import { saveAs } from 'file-saver';

const OPTIMIZATION_REPORT_MSG_GROUP = 'optimization-report';

@Component({
  selector: 'app-list-optimization-report',
  templateUrl: './list-optimization-report.component.html',
  styleUrls: ['./list-optimization-report.component.scss']
})
export class ListOptimizationReportComponent implements OnInit, OnDestroy {

  modelRun: ModelRun;

  metaData: MetaData;

  reports: Array<OptimizationReport>;

  subscriptions: Subscription;

  get projectId(): number {
    return this.modelRun.projectId;
  }

  get modelRunId(): string {
    return this.modelRun.id;
  }

  constructor(private route: ActivatedRoute,
              private optimizationReportService: OptimizationReportService,
              private scenarioService: ScenarioService,
              private router: Router,
              private uiBlockerService: UiBlockerService,
              private dialog: MatDialog,
              private modelRunService: ModelRunService,
              private snackBarService: SnackBarService,
              private appConstantsService: AppConstantsService,
              private webMessageService: WebMessageService,
              private ngZone: NgZone) {
  }

  ngOnInit(): void {
    this.metaData = this.route.parent.parent.snapshot.data.metaData;
    this.subscriptions = new Subscription();
    this.subscriptions.add(this.route.parent.parent.params.subscribe(() => {
      this.modelRun = this.modelRunService.activeModelRun;
      this.reports = this.optimizationReportService.getAll(this.projectId, this.modelRunId);
      this.webMessageService.consumeMessage(`${OPTIMIZATION_REPORT_MSG_GROUP}-${this.modelRunId}`).subscribe((eventData) => {
        const event = JSON.parse(eventData);
        const updatedReport = this.optimizationReportService.refreshCachedModel(event.data);
        if (updatedReport) {
          this.reports.splice(this.reports.findIndex(it => it.id === updatedReport.id), 1, updatedReport);
        }
      });
    }));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  createOptimizationRun(): void {
    this.router.navigate(['create'], {relativeTo: this.route.parent});
  }

  /**
   * Downloads optimization report file.
   * @see https://stackoverflow.com/questions/51960172/set-file-name-while-downloading-via-blob-in-angular-5
   */
  downloadOptimizationReport(optimizationReport: OptimizationReport, exportForSharePoint: boolean): void {
    this.uiBlockerService.block();
    if(!exportForSharePoint){
      this.optimizationReportService.generateReport(optimizationReport).subscribe((response) => {
        const blob = new Blob([response], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'});
        const sanitizedFileName = sanitizeFileName(optimizationReport.name) + '.xlsx';
        saveAs(blob, sanitizedFileName);
        this.uiBlockerService.unblock();
      }, error => {
        this.uiBlockerService.unblock();
        this.snackBarService.openErrorSnackBar('Failed downloading report.');
      });
    }
    else{
      this.optimizationReportService.powerBIReportGeneration(optimizationReport).subscribe((response) => {
        this.uiBlockerService.unblock();
        this.snackBarService.openSuccessSnackBar('Report exported to SharePoint successfully.');
      }, error => {
        this.uiBlockerService.unblock();
        const errorMessage = error.status == 423 ? 'Export failed because the file is currently open and cannot be replaced.' : 'Failed downloading report.';  
        this.snackBarService.openErrorSnackBar(errorMessage);
      });
    }
  }

  deleteOptimizationReport(optimizationReport: OptimizationReport): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '800px';
    dialogConfig.data = {
      header: 'Delete?',
      body: 'Are you sure you want to delete the report?',
      cancelButtonLabel: 'CANCEL',
      confirmButtonLabel: 'DELETE'
    };

    const dialogRef = this.dialog.open(GenericConfirmationModalComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(value => {
      if (value === 'DELETE') {
        this.uiBlockerService.block();
        this.optimizationReportService.delete(optimizationReport).subscribe(() => {
          this.uiBlockerService.unblock();
          this.reports = [...this.optimizationReportService.getAll(optimizationReport.projectId, optimizationReport.modelRunId)];
        }, error => {
          this.uiBlockerService.unblock();
        });
      }
    });
  }

  duplicate(report: OptimizationReport) {
    this.router.navigate(['create'], {
      relativeTo: this.route.parent,
      queryParams: {source: report.id}
    });
  }
}
